import { useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Modal, Input, Select, useToast } from '../../design-system';
import { isValidPan, fmtDate } from '../../lib/dsc';

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

// validFrom (or today, if blank) + N years, as a YYYY-MM-DD input value.
const addYears = (dateStr, years) => {
  const base = dateStr ? new Date(dateStr) : new Date();
  base.setFullYear(base.getFullYear() + years);
  return base.toISOString().slice(0, 10);
};

const emptyForm = {
  clientId: '',
  codeMode: 'auto', // 'auto' → system DSC-0001 | 'custom' → user types
  dscCode: '',
  name: '',
  pan: '',
  tokenSerial: '',
  validFrom: '',
  validToMode: '2', // '1' | '2' | '3' years | 'custom'
  validTo: '',
  associatedPerson: '',
  place: '',
  remarks: '',
};

// One modal for both add and edit. `dsc` null → create; otherwise edit that row.
function DscModal({ dsc, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = Boolean(dsc);
  const [form, setForm] = useState(emptyForm);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/api/clients').then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (dsc) {
      // Editing an existing DSC: it already has a code and a concrete date, so
      // both controls start in "custom" mode.
      setForm({
        ...emptyForm,
        ...dsc,
        codeMode: 'custom',
        clientId: dsc.clientId?._id || dsc.clientId || '',
        validFrom: toDateInput(dsc.validFrom),
        validToMode: 'custom',
        validTo: toDateInput(dsc.validTo),
      });
    } else {
      setForm(emptyForm);
    }
  }, [dsc]);

  // When Valid To is a 1/2/3-year term, keep the concrete date in step with
  // the term and the Valid From date. Custom mode leaves validTo untouched.
  useEffect(() => {
    if (form.validToMode === 'custom') return;
    const computed = addYears(form.validFrom, Number(form.validToMode));
    setForm((f) => (f.validTo === computed ? f : { ...f, validTo: computed }));
  }, [form.validFrom, form.validToMode]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // DSC codes are always stored uppercase — mirror that live in the field.
  const setCode = (e) => setForm((f) => ({ ...f, dscCode: e.target.value.toUpperCase() }));

  const useCustomCode = isEdit || form.codeMode === 'custom';

  const save = async (e) => {
    e?.preventDefault();
    setError('');

    if (useCustomCode && !form.dscCode.trim()) {
      return setError('Enter a custom DSC code, or switch to auto-generate');
    }
    if (!form.name.trim()) return setError('Name is required');
    if (form.pan.trim() && !isValidPan(form.pan)) {
      return setError('Invalid PAN format (expected AAAAA9999A)');
    }
    if (form.validFrom && form.validTo && form.validTo < form.validFrom) {
      return setError('Valid To cannot be before Valid From');
    }

    const payload = {
      clientId: form.clientId || null,
      name: form.name.trim(),
      pan: form.pan.trim().toUpperCase(),
      tokenSerial: form.tokenSerial.trim(),
      validFrom: form.validFrom || null,
      validTo: form.validTo || null,
      associatedPerson: form.associatedPerson.trim(),
      place: form.place.trim(),
      remarks: form.remarks.trim(),
    };
    // Auto mode sends no code — the backend assigns "DSC-0001" from the sequence.
    if (useCustomCode) payload.dscCode = form.dscCode.trim().toUpperCase();

    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put(`/api/dscs/${dsc._id}`, payload);
        toast.success('DSC updated');
      } else {
        await apiClient.post('/api/dscs', payload);
        toast.success('DSC added');
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save DSC');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button variant="primary" onClick={save} loading={saving}>{isEdit ? 'Save Changes' : 'Add DSC'}</Button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title={isEdit ? 'Edit DSC' : 'Add DSC'} footer={footer} size="lg">
      <form className="dsc-form" onSubmit={save}>
        {error && <div className="dsc-form-error" role="alert">{error}</div>}

        <div className="dsc-form-row">
          {isEdit ? (
            <Input label="DSC Code *" value={form.dscCode} onChange={setCode} placeholder="e.g. DSC-001" />
          ) : (
            <Select label="DSC Code *" value={form.codeMode} onChange={set('codeMode')}>
              <option value="auto">Auto-generate (DSC-0001)</option>
              <option value="custom">Custom code</option>
            </Select>
          )}
          <Input label="Name *" value={form.name} onChange={set('name')} placeholder="Certificate holder" />
        </div>
        {!isEdit && form.codeMode === 'custom' && (
          <Input label="Custom DSC code *" value={form.dscCode} onChange={setCode} placeholder="e.g. DSC-001" />
        )}
        {!isEdit && form.codeMode === 'auto' && (
          <p className="dsc-form-hint">A code like <strong>DSC-0001</strong> is assigned automatically when you save.</p>
        )}

        <Select label="Client" value={form.clientId} onChange={set('clientId')}>
          <option value="">— Firm's own / no client —</option>
          {clients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
        </Select>

        <div className="dsc-form-row">
          <Input label="PAN" value={form.pan} onChange={set('pan')} placeholder="AAAAA9999A" maxLength={10} />
          <Input label="Token Serial" value={form.tokenSerial} onChange={set('tokenSerial')} placeholder="USB token serial" />
        </div>

        <div className="dsc-form-row">
          <Input label="Valid From" type="date" value={form.validFrom} onChange={set('validFrom')} />
          <Select label="Valid To" value={form.validToMode} onChange={set('validToMode')}>
            <option value="1">1 year</option>
            <option value="2">2 years</option>
            <option value="3">3 years</option>
            <option value="custom">Custom date</option>
          </Select>
        </div>
        {form.validToMode === 'custom' ? (
          <Input label="Valid To (date)" type="date" value={form.validTo} onChange={set('validTo')} />
        ) : (
          form.validTo && <p className="dsc-form-hint">Expires on <strong>{fmtDate(form.validTo)}</strong></p>
        )}

        <div className="dsc-form-row">
          <Input label="Associated Person" value={form.associatedPerson} onChange={set('associatedPerson')} placeholder="Who holds the token" />
          <Input label="Place" value={form.place} onChange={set('place')} placeholder="Where it is kept" />
        </div>
        <Input label="Remarks" value={form.remarks} onChange={set('remarks')} placeholder="Company it's used for, notes…" />

        {/* ── Who touched it ────────────────────────────────────────────────
            Read-only, and edit-only because a new row has no history yet.

            ⚠️ THE RENEWAL LIST IS THE POINT, not the "last edited by" line
            above it. Moving `validTo` forward REPLACES the old expiry date in
            the record, so without this the question asked six months later —
            "was this certificate current when that return was signed, and who
            said so?" — has no answer anywhere. */}
        {isEdit && (dsc.updatedByName || (dsc.renewals || []).length > 0) && (
          <div className="dsc-form-history">
            {dsc.updatedByName && (
              <p className="dsc-form-hint">
                Last edited by <strong>{dsc.updatedByName}</strong>
                {dsc.updatedAt ? ` on ${fmtDate(dsc.updatedAt)}` : ''}
              </p>
            )}
            {(dsc.renewals || []).length > 0 && (
              <>
                <p className="dsc-form-hint"><strong>Renewals</strong></p>
                <ul className="dsc-renewal-list">
                  {[...dsc.renewals].reverse().map((r, i) => (
                    <li key={r._id || i}>
                      {r.renewedByName || 'Someone'} renewed it on {fmtDate(r.renewedAt)} —
                      {r.previousValidTo ? ` was valid to ${fmtDate(r.previousValidTo)},` : ' no previous expiry on file,'}
                      {' '}now {fmtDate(r.newValidTo)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

export default DscModal;
